import { useMemo, useState } from "react";
import { COURSES, inquiryInputSchema, type InquiryInput } from "@mizpah/shared";
import { submitInquiry, RequestError, type InquiryResult } from "../lib/api.js";

type Errors = Partial<Record<keyof InquiryInput, string>>;

function nextMonths(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 1; i <= n; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
    out.push(m.toLocaleString("en-US", { month: "long", year: "numeric" }));
  }
  return out;
}

export default function InquiryPage() {
  const months = useMemo(() => nextMonths(3), []);
  const [form, setForm] = useState({
    fullName: "", email: "", phone: "", mailingAddress: "",
    courseId: COURSES[0].id as string, preferredBatchMonth: months[0],
    comments: "", howHeard: "", website: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<InquiryResult | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Validate a single field on blur (email/phone) so users get immediate
  // feedback instead of waiting for submit. Uses the shared schema's field rule.
  const validateField = (k: "email" | "phone") => {
    if (!form[k]) { setErrors((er) => ({ ...er, [k]: undefined })); return; } // don't nag on empty until submit
    const r = inquiryInputSchema.shape[k].safeParse(form[k]);
    setErrors((er) => ({ ...er, [k]: r.success ? undefined : r.error.issues[0]?.message }));
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError("");
    const parsed = inquiryInputSchema.safeParse(form);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof InquiryInput;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      setResult(await submitInquiry(parsed.data));
    } catch (err) {
      if (err instanceof RequestError && err.api.fields) {
        setErrors(err.api.fields as Errors);
        setFormError("Please fix the highlighted fields.");
      } else {
        setFormError(
          "We couldn't submit your inquiry just now. Please try again, or call us at (847) 708-9266."
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) return <Confirmation name={form.fullName} email={form.email} courseId={form.courseId} brochureUrl={result.brochureUrl} />;

  const courseName = COURSES.find((c) => c.id === form.courseId)?.name ?? "";

  return (
    <>
      <section className="stage">
        <div className="blobs"><span className="blob b1" /><span className="blob b2" /></div>
        <div className="wrap">
          <nav>
            <div className="logo"><img className="brandmark" src="/mizpah-logo.jpg" alt="Mizpah Center for Allied Health Education" /> <span className="logo-name">Mizpah Center for Allied Health Education</span></div>
            <div className="callbox">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" /></svg>
              <span className="callnums">
                <a href="tel:+18477089266">(847) 708-9266</a>
                <a href="tel:+12244369371">(224) 436-9371</a>
              </span>
            </div>
          </nav>
          <header className="hero">
            <div>
              <h1>Start the career that starts with <em>care.</em></h1>
              <p className="tagline">Where We Build Your Future</p>
              <p className="lede">Tell us which program you're curious about - we'll send the details and a brochure today, and a real person follows up.</p>
              <div className="trust">
                <div className="chip"><span className="num">100+</span><span className="lbl">Students enrolled</span></div>
                <div className="chip"><span className="num">4 wks</span><span className="lbl">CNA program</span></div>
                <div className="chip"><span className="num">{COURSES.length}</span><span className="lbl">Programs</span></div>
              </div>
            </div>
            <div className="portrait">
              <div className="ring" />
              <div className="frame">
                <img alt="Nursing student smiling" src="/nurse-hero.jpg" />
              </div>
            </div>
          </header>
        </div>
        <svg className="wave" viewBox="0 0 1440 70" preserveAspectRatio="none" fill="#fffdfb"><path d="M0,38 C240,72 480,8 720,30 C960,52 1200,74 1440,40 L1440,70 L0,70 Z" /></svg>
      </section>

      <section className="formstage">
        <div className="wrap" style={{ padding: "0 14px" }}>
          <div className="formcard">
            <div className="aside">
              <h2>Ask about a program</h2>
              <p>Two minutes now. Course details in your inbox before your coffee's done.</p>
              <ul className="alist">
                {["Course-specific brochure, emailed instantly", "Next start dates & fees", "A personal follow-up within one business day"].map((t) => (
                  <li key={t}><span className="tk"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg></span> {t}</li>
                ))}
              </ul>

              <div className="aside-contact">
                <div className="ac-block">
                  <h5><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" /></svg> Call us</h5>
                  <a href="tel:+18477089266">(847) 708-9266</a>
                  <a href="tel:+12244369371">(224) 436-9371</a>
                </div>
                <div className="ac-block">
                  <h5><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg> Visit us</h5>
                  <a href="https://maps.google.com/?q=2720+S+River+Rd+Suite+2+Des+Plaines+IL+60018" target="_blank" rel="noopener noreferrer">2720 S River Rd, Suite #2<br />Des Plaines, IL 60018</a>
                  <a className="ac-dir" href="https://www.google.com/maps/dir/?api=1&destination=2720+S+River+Rd+Suite+2+Des+Plaines+IL+60018" target="_blank" rel="noopener noreferrer">Get directions →</a>
                </div>
                <p className="aside-cta">Give us a call before you visit.</p>
              </div>
            </div>

            <form onSubmit={onSubmit} noValidate>
              {formError && <div className="formerror" role="alert">{formError}</div>}
              <div className="g2">
                <Field label="Full name" required error={errors.fullName}>
                  <input value={form.fullName} onChange={set("fullName")} autoComplete="name" />
                </Field>
                <Field label="Email" required error={errors.email}>
                  <input type="email" inputMode="email" value={form.email} onChange={set("email")} onBlur={() => validateField("email")} autoComplete="email" />
                </Field>
                <Field label="Phone" required error={errors.phone}>
                  <input type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} onBlur={() => validateField("phone")} placeholder="(773) 555-0100" autoComplete="tel" />
                </Field>
                <Field label="Mailing address" error={errors.mailingAddress}>
                  <input value={form.mailingAddress} onChange={set("mailingAddress")} />
                </Field>
              </div>
              <Field label="Which program interests you?" required error={errors.courseId} full>
                <select value={form.courseId} onChange={set("courseId")}>
                  {COURSES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Preferred start month" required error={errors.preferredBatchMonth} full>
                <select value={form.preferredBatchMonth} onChange={set("preferredBatchMonth")}>
                  {months.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Anything you'd like us to know?" optional full>
                <textarea value={form.comments} onChange={set("comments")} placeholder="Questions about schedule, fees, weekend classes..." />
              </Field>
              <Field label="How did you hear about us?" optional full>
                <input value={form.howHeard} onChange={set("howHeard")} placeholder="Google, a friend, Facebook..." />
              </Field>
              {/* Honeypot: hidden from users; bots fill it and get silently dropped. */}
              <div className="hp" aria-hidden="true">
                <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
              </div>

              <button className="btn" type="submit" disabled={submitting}>
                {submitting ? <><span className="spinner" /> Sending...</> : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg> Request Information</>}
              </button>
              <p className="consent">By submitting this form, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and consent to be contacted about our programs. You can opt out of follow-up emails at any time.</p>
              <div className="note"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg> We'll email your {courseName ? "" : "course "}details &amp; brochure right away.</div>
            </form>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="wrap foot-bottom">© {new Date().getFullYear()} Mizpah Center for Allied Health Education &nbsp;·&nbsp; <a href="/privacy">Privacy Policy</a></div>
      </footer>
    </>
  );
}

function Field(props: {
  label: string; required?: boolean; optional?: boolean; full?: boolean; error?: string; children: React.ReactNode;
}) {
  return (
    <div className={`field${props.full ? " full" : ""}${props.error ? " error" : ""}`}>
      <label>
        {props.label}{props.required && <span className="req"> *</span>}
        {props.optional && <span className="opt"> - optional</span>}
      </label>
      {props.children}
      {props.error && (
        <span className="err"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg> {props.error}</span>
      )}
    </div>
  );
}

function Confirmation({ name, email, courseId, brochureUrl }: { name: string; email: string; courseId: string; brochureUrl?: string }) {
  const course = COURSES.find((c) => c.id === courseId)?.name ?? "your program";
  const first = name.trim().split(/\s+/)[0] || "there";
  return (
    <section className="stage">
      <div className="blobs"><span className="blob b1" /><span className="blob b2" /></div>
      <div className="wrap">
        <div className="confirm">
          <div className="burst"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg></div>
          <h2>Thanks, {first} - we got it.</h2>
          <p>We've emailed your <strong>{course}</strong> details and brochure to <strong>{email}</strong>.{brochureUrl ? " Grab the brochure below to get started." : " Don't see it? Check spam, or call us at (847) 708-9266."}</p>
          {brochureUrl && (
            <a className="broc" href={brochureUrl} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg> Download brochure (PDF)
            </a>
          )}
          <div className="nx">A program advisor will follow up personally within one business day.</div>
        </div>
      </div>
    </section>
  );
}
